import { type TaskDetailResponse } from "@swivu/client-api";
import { DateTime, Interval } from "luxon";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { _mockAssignedResources, RESOURCE_COLORS } from "./shared-data.js";
import { cn, ResizeablePanel, useIsGummy } from "@swivu/shared-ui";

type AssignedResource = NonNullable<
  TaskDetailResponse["assigned_resources"]
>[number];

// Sub-components
interface TimelineHeaderProps {
  dates: DateTime[];
  dayWidth: number;
  headerHeight: number;
  todayIndex: number;
  selectedDate: DateTime | null;
  onDateClick: (date: DateTime) => void;
  compactDateLabels: boolean;
  isGummy: boolean;
}

const renderTimelineDateLabel = (
  date: DateTime,
  compactDateLabels: boolean
) => {
  const showsYear = Math.abs(date.diffNow("day").days) > 365;

  if (!compactDateLabels) {
    return showsYear ? date.toFormat("MMM d, yy") : date.toFormat("MMM d");
  }

  if (!showsYear) {
    return <span className="whitespace-nowrap">{date.toFormat("MMM d")}</span>;
  }

  return (
    <>
      <span className="whitespace-nowrap">{date.toFormat("MMM d,")}</span>
      <span className="whitespace-nowrap">{date.toFormat("yy")}</span>
    </>
  );
};

const TimelineHeader = ({
  dates,
  dayWidth,
  headerHeight,
  todayIndex,
  selectedDate,
  onDateClick,
  compactDateLabels,
  isGummy
}: TimelineHeaderProps) => (
  <div
    className={cn(
      "sticky top-0 z-10",
      isGummy
        ? "bg-mono-100/70 dark:bg-mono-800/50"
        : "bg-mono-50 dark:bg-mono-925 border-mono-200 dark:border-mono-700 border-b"
    )}
    style={{ height: `${headerHeight}px` }}
  >
    {dates.map((date, index) => {
      const isToday = index === todayIndex;
      const isSelected = selectedDate && date.hasSame(selectedDate, "day");
      const isHighlighted = isSelected || (isToday && !selectedDate);
      return (
        <div
          key={index}
          onClick={() => onDateClick(date)}
          className={cn(
            "absolute flex cursor-pointer flex-col items-center justify-center",
            isGummy
              ? cn(
                  "rounded-surface-md transition-transform duration-150 active:scale-[0.96] motion-reduce:transform-none",
                  isHighlighted
                    ? "z-10 bg-tile-primary text-white shadow-raised text-shadow-on-color"
                    : "hover:bg-primary-100/50 dark:hover:bg-primary-900/30"
                )
              : cn(
                  "border-mono-200 dark:border-mono-700 border-r last:border-r-0 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20",
                  isHighlighted
                    ? "border-blue-300 bg-blue-100 dark:border-blue-600 dark:bg-blue-900/30"
                    : "border-t"
                )
          )}
          style={{
            left: `${index * dayWidth}px`,
            width: `${dayWidth}px`,
            height: `${headerHeight}px`
          }}
        >
          {isGummy && isHighlighted && headerHeight >= 56 && (
            <span aria-hidden className="top-gloss" />
          )}
          <div
            className={cn(
              "mobile-pref-card-meta relative flex flex-col items-center px-1 text-center text-xs leading-tight",
              isGummy ? "font-display font-black" : "font-medium",
              isHighlighted
                ? isGummy
                  ? "text-white"
                  : "text-blue-700 dark:text-blue-300"
                : isGummy
                  ? "text-mono-700 dark:text-mono-300"
                  : "text-mono-600 dark:text-mono-400"
            )}
          >
            {renderTimelineDateLabel(date, compactDateLabels)}
          </div>
          <div
            className={cn(
              "mobile-pref-card-meta relative px-1 text-center text-[11px] leading-tight whitespace-nowrap uppercase tracking-wider",
              isGummy && "font-display font-black",
              isHighlighted
                ? isGummy
                  ? "text-white/90"
                  : "text-blue-600 dark:text-blue-400"
                : isGummy
                  ? "text-mono-500 dark:text-mono-400"
                  : "text-mono-500 dark:text-mono-500"
            )}
          >
            {date.toFormat("EEE")}
          </div>
        </div>
      );
    })}
  </div>
);

interface ResourceBarProps {
  resource: any;
  dayWidth: number;
  rowHeight: number;
  headerHeight: number;
  resourceUrl: string;
  isGummy: boolean;
}

const ResourceBar = ({
  resource,
  dayWidth,
  rowHeight,
  headerHeight,
  resourceUrl,
  isGummy
}: ResourceBarProps) => (
  <Link
    to={resourceUrl.replace(":id", resource.resource_id.toString())}
    className={cn(
      "mobile-pref-card absolute flex items-center overflow-hidden px-4",
      resource.color.bg,
      isGummy
        ? cn(
            "rounded-surface-lg shadow-raised",
            "transition-transform duration-150 gummy-lift active:translate-y-0 active:scale-[0.97]",
            "motion-reduce:transform-none motion-reduce:transition-none"
          )
        : "rounded-md shadow-sm"
    )}
    style={{
      left: `${resource.startPosition * dayWidth + 2}px`,
      top: `${headerHeight + resource.row * rowHeight + 4}px`,
      width: `${resource.duration * dayWidth - 4}px`,
      height: `${rowHeight - 8}px`,
      ...(isGummy ? { textShadow: "var(--text-shadow-on-color)" } : null)
    }}
  >
    {isGummy && rowHeight >= 36 && <span aria-hidden className="top-gloss" />}
    <span
      className={cn(
        "mobile-pref-card-meta relative flex-1 truncate text-sm text-white",
        isGummy ? "font-display font-black tracking-tight" : "font-medium"
      )}
    >
      {resource.label}
      {resource.is_assigned_to_me && " *"}
    </span>
    <span
      className={cn(
        "mobile-pref-card-meta relative ml-2 whitespace-nowrap text-xs text-white/90",
        isGummy && "font-display font-black"
      )}
    >
      {resource.duration}d
    </span>
  </Link>
);

interface GridLinesProps {
  dates: DateTime[];
  dayWidth: number;
  headerHeight: number;
  maxRow: number;
  rowHeight: number;
}

const GridLines = ({
  dates,
  dayWidth,
  headerHeight,
  maxRow,
  rowHeight
}: GridLinesProps) => (
  <>
    {/* Vertical grid lines */}
    {dates.map((_, index) => (
      <div
        key={`grid-line-${index}`}
        className="border-mono-200 dark:border-mono-700 absolute border-r opacity-30"
        style={{
          left: `${(index + 1) * dayWidth - 1}px`,
          top: `${headerHeight}px`,
          width: "1px",
          height: `${(maxRow + 1) * rowHeight}px`
        }}
      />
    ))}

    {/* Row separators */}
    {Array.from({ length: maxRow + 1 }, (_, index) => (
      <div
        key={`row-line-${index}`}
        className="border-mono-200 dark:border-mono-700 absolute border-b opacity-20"
        style={{
          left: "0px",
          top: `${headerHeight + (index + 1) * rowHeight - 1}px`,
          width: `${dates.length * dayWidth}px`,
          height: "1px"
        }}
      />
    ))}
  </>
);

interface SelectedDayIndicatorProps {
  selectedIndex: number;
  dayWidth: number;
  headerHeight: number;
  maxRow: number;
  rowHeight: number;
}

const SelectedDayIndicator = ({
  selectedIndex,
  dayWidth,
  headerHeight,
  maxRow,
  rowHeight
}: SelectedDayIndicatorProps) => {
  if (selectedIndex < 0) return null;
  return (
    <div
      className="pointer-events-none absolute z-20 bg-blue-400 opacity-30 dark:bg-blue-500"
      style={{
        left: `${selectedIndex * dayWidth + dayWidth / 2 - 1}px`,
        top: `${headerHeight}px`,
        width: "2px",
        height: `${(maxRow + 1) * rowHeight}px`
      }}
    />
  );
};

interface LegendProps {
  uniqueResourceIds: number[];
  resources: any[];
  resourceUrl: string;
  selectedDate: DateTime | null;
  timezone: string;
  width?: number;
  isGummy: boolean;
}

const Legend = ({
  uniqueResourceIds,
  resources,
  resourceUrl,
  selectedDate,
  timezone,
  width,
  isGummy
}: LegendProps) => {
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    setShowAll(false);
  }, [selectedDate]);

  // Filter resources by selected date (or today if no date selected)
  const today = DateTime.now().setZone(timezone);
  const dateToFilter = (selectedDate || today).setZone(timezone).startOf("day");

  const filteredResourceIds = uniqueResourceIds.filter((id) => {
    return resources.some((r) => {
      if (r.resource_id !== id) return false;

      // Use the same date adjustment logic as in the Gantt chart
      const startDate = r.startDate.setZone(timezone).startOf("day");
      const endDateInTimezone = r.endDate.setZone(timezone);

      // Adjust end date if it's exactly at midnight (same as Gantt logic)
      const adjustedEndDate = endDateInTimezone.equals(
        endDateInTimezone.startOf("day")
      )
        ? endDateInTimezone.minus({ seconds: 1 })
        : endDateInTimezone;
      const endDate = adjustedEndDate.startOf("day");

      // Check if the filter date falls within the resource's date range
      return dateToFilter >= startDate && dateToFilter <= endDate;
    });
  });

  // Sort resource IDs by label
  const sortedResourceIds = [...filteredResourceIds].sort((a, b) => {
    const resourceA = resources.find((r) => r.resource_id === a);
    const resourceB = resources.find((r) => r.resource_id === b);
    return resourceA.label.localeCompare(resourceB.label);
  });

  const maxVisible = 3;
  const hasMoreThanMax = sortedResourceIds.length > maxVisible;
  const visibleResourceIds = showAll
    ? sortedResourceIds
    : sortedResourceIds.slice(0, maxVisible);
  const additionalCount = sortedResourceIds.length - maxVisible;
  return (
    <ResizeablePanel>
      <div
        className={cn(
          "@container",
          width === undefined && "min-w-full",
          isGummy
            ? "rounded-t-surface-xl  p-4  shadow-card"
            : " rounded-t-md border-x border-y border-border p-3"
        )}
        style={width ? { width: `${width}px` } : undefined}
      >
        <div
          className={cn(
            "grid grid-cols-[repeat(auto-fit,minmax(100px,max-content))]",
            isGummy ? "gap-x-4 gap-y-1.5" : "gap-x-4 gap-y-1.5"
          )}
        >
          {visibleResourceIds.length > 0 ? (
            visibleResourceIds.map((resourceId) => {
              const resource = resources.find(
                (r) => r.resource_id === resourceId
              )!;
              return (
                <Link
                  className={cn(
                    "min-w-0 self-center",
                    isGummy
                      ? "flex min-h-11 items-center transition-transform duration-150 gummy-lift active:translate-y-0 active:scale-[0.97] motion-reduce:transform-none"
                      : ""
                  )}
                  key={resourceId}
                  to={resourceUrl.replace(":id", resourceId.toString())}
                >
                  <div
                    className={cn(
                      "flex min-w-0 items-center",
                      isGummy ? "gap-3" : "gap-2"
                    )}
                  >
                    <div
                      className={cn(
                        "flex-none",
                        resource.color.bg,
                        isGummy
                          ? "h-5 w-5 rounded-full shadow-pill"
                          : "h-3 w-3 rounded"
                      )}
                      aria-hidden="true"
                    />
                    <span
                      className={cn(
                        "mobile-pref-card-meta truncate",
                        isGummy
                          ? "text-sm font-display font-black text-mono-800 dark:text-mono-100"
                          : "text-xs text-mono-600 dark:text-mono-400"
                      )}
                    >
                      {resource.label}
                      {resource.is_assigned_to_me && " *"}
                    </span>
                  </div>
                </Link>
              );
            })
          ) : (
            <div
              className={cn(
                "mobile-pref-card-meta",
                isGummy
                  ? "pl-1 text-sm font-display font-black text-mono-700 dark:text-mono-200"
                  : "pl-3 text-xs text-mono-600 dark:text-mono-400"
              )}
            >
              No resources scheduled for {dateToFilter.toFormat("MMMM d, yyyy")}
            </div>
          )}
          {showAll && hasMoreThanMax && (
            <button
              onClick={() => setShowAll(false)}
              className={cn(
                "@sm:col-span-1 mobile-pref-inline-button col-span-full mr-auto block",
                isGummy
                  ? "min-h-11 text-sm font-display font-black text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                  : "text-xs text-blue-600 underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              )}
            >
              Show Less
            </button>
          )}
          {hasMoreThanMax && !showAll && (
            <div
              className={cn(
                "@sm:col-span-1 col-span-full flex items-center",
                isGummy ? "min-h-11 gap-3" : "gap-2"
              )}
            >
              <span
                className={cn(
                  "mobile-pref-card-meta text-mono-500 dark:text-mono-500",
                  isGummy ? "text-sm font-display font-black" : "text-xs"
                )}
              >
                + {additionalCount} additional
              </span>
              <button
                onClick={() => setShowAll(true)}
                className={cn(
                  "mobile-pref-inline-button",
                  isGummy
                    ? "text-sm font-display font-black text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200"
                    : "text-xs text-blue-600 underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                )}
              >
                View All
              </button>
            </div>
          )}
        </div>
        {sortedResourceIds.some((id) => {
          const resource = resources.find((r) => r.resource_id === id);
          return resource?.is_assigned_to_me;
        }) && (
          <div
            className={cn(
              "mobile-pref-card-meta text-mono-500 dark:text-mono-500",
              isGummy
                ? "mt-3 text-xs font-display font-black uppercase tracking-widest"
                : "mt-2 text-xs"
            )}
          >
            * Assigned to you
          </div>
        )}
      </div>
    </ResizeablePanel>
  );
};

interface GanttChartProps {
  dates: DateTime[];
  resources: any[];
  maxRow: number;
  dayWidth: number;
  rowHeight: number;
  headerHeight: number;
  compactDateLabels: boolean;
  todayIndex: number;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  ganttContentRef: React.RefObject<HTMLDivElement | null>;
  resourceUrl: string;
  legend: React.ReactNode;
  selectedDate: DateTime | null;
  onDateClick: (date: DateTime) => void;
  isGummy: boolean;
}

const GanttChart = ({
  dates,
  resources,
  maxRow,
  dayWidth,
  rowHeight,
  headerHeight,
  compactDateLabels,
  todayIndex,
  scrollContainerRef,
  ganttContentRef,
  resourceUrl,
  legend,
  selectedDate,
  onDateClick,
  isGummy
}: GanttChartProps) => {
  const contentRef = ganttContentRef;

  return (
    <div>
      {legend}
      <div
        ref={scrollContainerRef}
        className="relative -translate-y-0.5 overflow-x-auto"
      >
        <div
          ref={contentRef}
          className={cn(
            "inline-block",
            isGummy
              ? "bg-white dark:bg-mono-900 sm:rounded-b-surface-xl shadow-card"
              : "border-mono-200 dark:border-mono-700 bg-mono-50 dark:bg-mono-925 border-b sm:rounded-b-lg sm:border-x sm:border-b"
          )}
        >
          <div
            className="relative"
            style={{
              width: `${dates.length * dayWidth}px`,
              height: `${headerHeight + (maxRow + 1) * rowHeight}px`
            }}
          >
            <TimelineHeader
              dates={dates}
              dayWidth={dayWidth}
              headerHeight={headerHeight}
              compactDateLabels={compactDateLabels}
              todayIndex={todayIndex}
              selectedDate={selectedDate}
              onDateClick={onDateClick}
              isGummy={isGummy}
            />
            <GridLines
              dates={dates}
              dayWidth={dayWidth}
              headerHeight={headerHeight}
              maxRow={maxRow}
              rowHeight={rowHeight}
            />
            {/* Resource bars */}
            {resources.map((resource) => (
              <ResourceBar
                key={resource.index}
                resource={resource}
                dayWidth={dayWidth}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                resourceUrl={resourceUrl}
                isGummy={isGummy}
              />
            ))}
            <SelectedDayIndicator
              selectedIndex={
                selectedDate
                  ? dates.findIndex((date) => date.hasSame(selectedDate, "day"))
                  : todayIndex
              }
              dayWidth={dayWidth}
              headerHeight={headerHeight}
              maxRow={maxRow}
              rowHeight={rowHeight}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

interface SummaryInfoProps {
  resources: any[];
  dates: DateTime[];
  timezone: string;
  isTodayAssigned: boolean;
  isGummy: boolean;
}

const SummaryInfo = ({
  resources,
  dates,
  timezone,
  isTodayAssigned,
  isGummy
}: SummaryInfoProps) => {
  const resouceCount = new Set(resources.map((r) => r.resource_id)).size;
  if (!isTodayAssigned) {
    // remove today from dates
    dates = dates.filter(
      (date) => !date.hasSame(DateTime.now().setZone(timezone), "day")
    );
  }
  return (
    <div
      className={cn(
        "mobile-pref-card-meta mt-4 px-5 sm:px-0 text-mono-600 dark:text-mono-300",
        isGummy ? "text-sm font-display font-black" : "text-xs"
      )}
    >
      {resouceCount} resource{resouceCount !== 1 ? "s" : ""} assigned
      {dates.length > 0 && (
        <>
          <span className="mx-2 text-mono-400 dark:text-mono-600">•</span>
          <span>
            {dates[0].toFormat("MMM d")} –{" "}
            {dates[dates.length - 1].toFormat("MMM d, yyyy")}
          </span>
          <span className="mx-2 text-mono-400 dark:text-mono-600">•</span>
          <span className="hidden">
            {DateTime.now().setZone(timezone).toFormat("ZZZZ")}
          </span>
        </>
      )}
    </div>
  );
};

type TimelineLayout = {
  dayWidth: number;
  rowHeight: number;
  headerHeight: number;
  compactDateLabels: boolean;
};

const DESKTOP_LAYOUT: TimelineLayout = {
  dayWidth: 96,
  rowHeight: 48,
  headerHeight: 56,
  compactDateLabels: false
};

const getTimelineLayout = (): TimelineLayout => {
  if (typeof window === "undefined") return DESKTOP_LAYOUT;

  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  if (!isMobile) return DESKTOP_LAYOUT;

  const textScale = document.body.dataset.textScale ?? "default";
  const touchDensity = document.body.dataset.touchDensity ?? "default";

  if (textScale === "x-large" && touchDensity === "large") {
    return {
      dayWidth: 104,
      rowHeight: 64,
      headerHeight: 88,
      compactDateLabels: true
    };
  }
  if (textScale === "x-large") {
    return {
      dayWidth: 96,
      rowHeight: 60,
      headerHeight: 80,
      compactDateLabels: true
    };
  }
  if (textScale === "large" || touchDensity === "large") {
    return {
      dayWidth: 88,
      rowHeight: 56,
      headerHeight: 72,
      compactDateLabels: true
    };
  }
  if (touchDensity === "comfortable") {
    return {
      dayWidth: 80,
      rowHeight: 52,
      headerHeight: 64,
      compactDateLabels: false
    };
  }
  return {
    dayWidth: 72,
    rowHeight: 48,
    headerHeight: 56,
    compactDateLabels: false
  };
};

// Main component
const AssignedResources = ({
  assigned_resources,
  timezone,
  resourceUrl,
  isTodayAssigned
}: {
  assigned_resources: AssignedResource[] | null | undefined;
  timezone: string;
  resourceUrl: string;
  isTodayAssigned: boolean;
}) => {
  const isGummy = useIsGummy();
  // test dev build
  // assigned_resources = _mockAssignedResources;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const ganttContentRef = useRef<HTMLDivElement>(null);
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);
  const [ganttWidth, setGanttWidth] = useState<number | undefined>(undefined);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined
  );
  const [timelineLayout, setTimelineLayout] = useState(getTimelineLayout);

  const hasResources = assigned_resources && assigned_resources.length > 0;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const updateTimelineLayout = () => {
      setTimelineLayout(getTimelineLayout());
    };

    updateTimelineLayout();

    const observer = new MutationObserver(updateTimelineLayout);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-text-scale", "data-touch-density"]
    });

    mediaQuery.addEventListener("change", updateTimelineLayout);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", updateTimelineLayout);
    };
  }, []);

  // Auto-scroll to date closest to today - only on initial mount
  useEffect(() => {
    if (!hasResources || !scrollContainerRef.current || selectedDate) return;

    const today = DateTime.now().setZone(timezone).startOf("day");
    const dayWidth = timelineLayout.dayWidth;

    // We need to compute dates here to find closest index
    // This duplicates some logic but keeps hooks unconditional
    const assignedDatesSet = new Set<string>();
    assignedDatesSet.add(today.toISODate()!);

    assigned_resources.forEach((resource) => {
      if (!resource.start_date) return;
      const startDate = DateTime.fromISO(resource.start_date)
        .setZone(timezone)
        .startOf("day");
      const endDateInTimezone = DateTime.fromISO(
        resource.end_date || resource.start_date
      ).setZone(timezone);
      const adjustedEndDate = endDateInTimezone.equals(
        endDateInTimezone.startOf("day")
      )
        ? endDateInTimezone.minus({ seconds: 1 })
        : endDateInTimezone;
      const endDate = adjustedEndDate.startOf("day");

      const startDateKey = startDate.toISODate();
      if (startDateKey) assignedDatesSet.add(startDateKey);
      const endDateKey = endDate.toISODate();
      if (endDateKey) assignedDatesSet.add(endDateKey);

      if (!startDate.equals(endDate)) {
        const interval = Interval.fromDateTimes(
          startDate,
          endDate.plus({ days: 1 })
        );
        interval.splitBy({ days: 1 }).forEach((dayInterval) => {
          if (dayInterval.start) {
            const dayDate = dayInterval.start.toISODate();
            if (dayDate) assignedDatesSet.add(dayDate);
          }
        });
      }
    });

    const dates = Array.from(assignedDatesSet)
      .sort()
      .map((dateStr) => DateTime.fromISO(dateStr).setZone(timezone));

    if (dates.length === 0) return;

    let closestIndex = 0;
    let minDiff = Math.abs(dates[0].diff(today, "days").days);
    dates.forEach((date, index) => {
      const diff = Math.abs(date.diff(today, "days").days);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = index;
      }
    });

    const containerWidth = scrollContainerRef.current.clientWidth;
    const targetScrollLeft =
      closestIndex * dayWidth - containerWidth / 2 + dayWidth / 2;
    const maxScrollLeft = dates.length * dayWidth - containerWidth;
    scrollContainerRef.current.scrollLeft = Math.max(
      0,
      Math.min(targetScrollLeft, maxScrollLeft)
    );
  }, [
    assigned_resources,
    hasResources,
    selectedDate,
    timelineLayout.dayWidth,
    timezone
  ]);

  // Monitor Gantt chart and container widths
  useEffect(() => {
    if (!ganttContentRef.current || !scrollContainerRef.current) return;

    const contentObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setGanttWidth(entry.contentRect.width);
      }
    });

    const containerObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    contentObserver.observe(ganttContentRef.current);
    containerObserver.observe(scrollContainerRef.current);

    return () => {
      contentObserver.disconnect();
      containerObserver.disconnect();
    };
  }, []);

  // Calculate timeline data
  if (!hasResources) return null;

  // Create color mapping for unique resource IDs
  const uniqueResourceIds = Array.from(
    new Set(assigned_resources.map((r) => r.resource_id))
  );
  const resourceColorMap = new Map<number, (typeof RESOURCE_COLORS)[0]>();
  uniqueResourceIds.forEach((resourceId, index) => {
    resourceColorMap.set(
      resourceId,
      RESOURCE_COLORS[index % RESOURCE_COLORS.length]
    );
  });

  let timelineData = null;
  if (assigned_resources && assigned_resources.length > 0) {
    // Process resources and find timeline bounds
    const processedResources = assigned_resources
      .map((resource, index) => {
        if (!resource.start_date) return null;

        const startDate = DateTime.fromISO(resource.start_date);
        // .setZone(timezone)
        // .startOf("day");
        const endDate = DateTime.fromISO(
          resource.end_date || resource.start_date
        );

        const color = resourceColorMap.get(resource.resource_id)!;

        return {
          ...resource,
          startDate,
          endDate,
          index,
          color
        };
      })
      .filter((resource): resource is NonNullable<typeof resource> =>
        Boolean(resource)
      );

    if (processedResources.length === 0) return null;

    // Collect all dates that have resources assigned (using same logic as positioning)
    const assignedDatesSet = new Set<string>();
    assignedDatesSet.add(DateTime.now().setZone(timezone).toISODate()!);
    processedResources.forEach((resource) => {
      // Use the same date adjustment logic as in positioning
      const startDate = resource.startDate.setZone(timezone).startOf("day");
      const endDateInTimezone = resource.endDate.setZone(timezone);
      const adjustedEndDate = endDateInTimezone.equals(
        endDateInTimezone.startOf("day")
      )
        ? endDateInTimezone.minus({ seconds: 1 })
        : endDateInTimezone;
      const endDate = adjustedEndDate.setZone(timezone).startOf("day");

      // Add start date
      const startDateKey = startDate.toISODate();
      if (startDateKey) {
        assignedDatesSet.add(startDateKey);
      }

      // Add end date (which may be the same as start date)
      const endDateKey = endDate.toISODate();
      if (endDateKey) {
        assignedDatesSet.add(endDateKey);
      }

      // Add all dates between start and end (if multi-day)
      if (!startDate.equals(endDate)) {
        const interval = Interval.fromDateTimes(
          startDate,
          endDate.plus({ days: 1 })
        );
        const days = interval.splitBy({ days: 1 });
        days.forEach((dayInterval) => {
          if (dayInterval.start) {
            const dayDate = dayInterval.start.toISODate();
            if (dayDate) {
              assignedDatesSet.add(dayDate);
            }
          }
        });
      }
    });

    // Convert to sorted array of DateTime objects
    const timelineDays = Array.from(assignedDatesSet)
      .sort()
      .map((dateStr) => DateTime.fromISO(dateStr).setZone(timezone));

    // Create date to index mapping for positioning
    const dateToIndex = new Map<string, number>();
    timelineDays.forEach((date, index) => {
      dateToIndex.set(date.toISODate()!, index);
    });

    // Calculate positions for each resource based on timeline indices
    const resourcesWithPositions = processedResources
      .map((resource) => {
        const startDateKey = resource.startDate
          .setZone(timezone)
          .startOf("day")
          .toISODate()!;
        const startIndex = dateToIndex.get(startDateKey);

        if (startIndex === undefined) {
          return null;
        }

        const endDateInTimezone = resource.endDate.setZone(timezone);
        const adjustedEndDate = endDateInTimezone.equals(
          endDateInTimezone.startOf("day")
        )
          ? endDateInTimezone.minus({ seconds: 1 })
          : endDateInTimezone;
        const endDateKey = adjustedEndDate.startOf("day").toISODate()!;
        const endIndex = dateToIndex.get(endDateKey);

        // If endIndex is not found, default to startIndex (single day duration)
        const duration = (endIndex ?? startIndex) - startIndex + 1;

        return {
          ...resource,
          startPosition: startIndex,
          duration,
          row: 0 // Will be calculated for overlaps
        };
      })
      .filter(
        (resource): resource is NonNullable<typeof resource> =>
          resource !== null
      );

    // Calculate row positions to handle overlaps (stack resources)
    const sortedResources = [...resourcesWithPositions].sort(
      (a, b) => a.startPosition - b.startPosition
    );

    sortedResources.forEach((resource) => {
      const endPosition = resource.startPosition + resource.duration - 1;
      let row = 0;

      // Find the lowest available row
      while (true) {
        const hasOverlap = sortedResources.some((other) => {
          if (other === resource || other.row !== row) return false;
          const otherEnd = other.startPosition + other.duration - 1;
          return (
            resource.startPosition <= otherEnd &&
            endPosition >= other.startPosition
          );
        });

        if (!hasOverlap) {
          resource.row = row;
          break;
        }
        row++;
      }
    });

    const maxRow = Math.max(...resourcesWithPositions.map((r) => r.row), 0);

    timelineData = {
      dates: timelineDays,
      resources: resourcesWithPositions,
      maxRow,
      timezone
    };
  }

  if (!timelineData) return null;

  const { dates, resources, maxRow } = timelineData;

  // Timeline configuration
  const { dayWidth, rowHeight, headerHeight, compactDateLabels } =
    timelineLayout;

  // Check if today is in the timeline
  const today = DateTime.now().setZone(timezone).startOf("day");
  const todayIndex = dates.findIndex((date) => date.hasSame(today, "day"));

  return (
    <div
      className={cn(
        "-mx-5 mt-4 pt-4 sm:mx-0",
        isGummy ? "" : "border-mono-100 dark:border-mono-800 border-t"
      )}
    >
      <div className="px-5 sm:px-0">
        <h4
          className={cn(
            "mb-3",
            isGummy
              ? "text-lg font-display font-black uppercase tracking-widest text-mono-600 dark:text-mono-300"
              : "text-sm font-medium text-mono-500 dark:text-mono-400"
          )}
        >
          Assigned Resources Timeline
        </h4>
      </div>

      <GanttChart
        dates={dates}
        resources={resources}
        maxRow={maxRow}
        dayWidth={dayWidth}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        compactDateLabels={compactDateLabels}
        todayIndex={todayIndex}
        scrollContainerRef={scrollContainerRef}
        ganttContentRef={ganttContentRef}
        resourceUrl={resourceUrl}
        selectedDate={selectedDate}
        onDateClick={setSelectedDate}
        isGummy={isGummy}
        legend={
          <Legend
            uniqueResourceIds={uniqueResourceIds.filter((id) =>
              resources.some((r) => r.resource_id === id)
            )}
            resources={resources}
            resourceUrl={resourceUrl}
            selectedDate={selectedDate}
            timezone={timezone}
            isGummy={isGummy}
            width={
              ganttWidth && containerWidth
                ? ganttWidth <= containerWidth
                  ? ganttWidth + 2 // When content fits: add border width
                  : containerWidth // When overflow: use full container width
                : undefined
            }
          />
        }
      />

      <SummaryInfo
        resources={resources}
        dates={dates}
        timezone={timezone}
        isTodayAssigned={isTodayAssigned}
        isGummy={isGummy}
      />
    </div>
  );
};

export default AssignedResources;
